import React from 'react';
import _ from 'lodash';

import {
  DEFAULT_LOGIN_URL,
  DEFAULT_SIGN_UP_URL,
  SignupType,
} from '@zapier/auth';

import { Profile } from '@zapier/shared-entities';
import ActivateLinkFormContainer from 'app/login/components/ActivateLinkFormContainer';
import ConfirmSecureSessionContainer from 'app/login/components/ConfirmSecureSessionContainer';
import CookiesDisabledFormContainer from 'app/login/components/CookiesDisabledFormContainer';
import ForgotFormContainer from 'app/login/components/ForgotFormContainer';
import LockedFormContainer from 'app/login/components/LockedFormContainer';
import LoginApp from 'app/login/components/App';
import LoginFormContainer from 'app/login/components/LoginFormContainer';
import PlatformHeaderContainer from 'app/viewport/components/PlatformHeaderContainer';
import ResetFormContainer from 'app/login/components/ResetFormContainer';
import SignUpFormContainer from 'app/login/components/SignUpFormContainer';
import TrackData from 'app/track/Data';
import TwoFactorAuthFormContainer from 'app/login/components/TwoFactorAuthFormContainer';
import withGlobalErrorModal from 'app/common/components/withGlobalErrorModal';
import { maybeUnsafeRouteOptions } from 'app/common/utils/RouterUtils';

const createLoginRoute = ({
  Component,
  promo = 'login',
  promoClass,
  renderHeader,
}) => ({
  FormComponent: withGlobalErrorModal(Component),
  component: LoginApp,
  promo,
  promoClass,
  previewType: 'login',
  isAuthRequired: false,
  assign: { renderHeader },
});

export default {
  '/app/login': createLoginRoute({ Component: LoginFormContainer }),
  '/app/login/2fa': createLoginRoute({ Component: TwoFactorAuthFormContainer }),
  '/app/login/secure-session': createLoginRoute({
    Component: ConfirmSecureSessionContainer,
  }),
  '/app/login/forgot': createLoginRoute({ Component: ForgotFormContainer }),

  // TODO: remove this route once email template is changed to /app/login/reset-password
  '/app/reset-password': createLoginRoute({ Component: ResetFormContainer }),

  '/app/login/reset-password': createLoginRoute({
    Component: ResetFormContainer,
  }),
  '/app/login/locked': createLoginRoute({ Component: LockedFormContainer }),
  '/app/login/cookies-disabled': createLoginRoute({
    Component: CookiesDisabledFormContainer,
  }),
  '/app/login/activate': createLoginRoute({
    Component: ActivateLinkFormContainer,
  }),

  '/sign-up/team-member/:appId': createLoginRoute({
    Component: SignUpFormContainer,
    promo: 'teamMember',
  }),
  '/platform/login': createLoginRoute({
    Component: LoginFormContainer,
    promo: 'platformLogin',
    promoClass: 'platform',
    renderHeader: () => <PlatformHeaderContainer />,
  }),
  '/platform/sign-up': createLoginRoute({
    Component: SignUpFormContainer,
    promo: 'platformSignUp',
    promoClass: 'platform',
    renderHeader: () => <PlatformHeaderContainer />,
  }),
  '/sign-up/sso/success': {
    onEnter({ routeTo, action }) {
      const nextUrl = _.get(
        action.meta,
        ['assign', 'query', 'next'],
        DEFAULT_SIGN_UP_URL
      );
      const useCaseSlug =
        _.get(action.meta, ['assign', 'query', 'use_case'], '') ||
        _.get(action.meta, ['assign', 'query', 'use_case_slug'], '');

      TrackData.trackSignupConversion(SignupType.sso, { useCaseSlug });

      // Force a hard refresh if we're in the middle of an OAuth flow, which lets Django redirect the user to the proper place.
      // TODO: we may need to add a middleware or another route which does a hard refresh
      const shouldExit = nextUrl.startsWith('/oauth/authorize/');

      routeTo(nextUrl, { replace: true, exit: shouldExit });
    },
  },
  '/sign-up': {
    ...createLoginRoute({ Component: SignUpFormContainer, promo: 'sign-up' }),
    previewType: 'sign-up',
    onEnter({ routeTo, getState, action }) {
      // Don't redirect if back/forward button is clicked.
      if (action.payload.isHistoryChange) {
        return;
      }
      const state = getState();
      // Go ahead and redirect if we have a profile.
      if (Profile.selectors.all.currentProfile(state)) {
        routeTo(
          _.get(action.meta, ['assign', 'query', 'next'], DEFAULT_LOGIN_URL),
          maybeUnsafeRouteOptions({
            replace: true,
          })
        );
      }
    },
    '/app/signup': {
      onEnter({ routeTo }) {
        routeTo('/sign-up');
      },
    },
  },
};
